import { eAlertSeverity, eAlertStatus, eAlertTypeGroup, eTrackedAlertTypeEntity } from './enums';
import { SelectOption } from './interfaces/select-option';
import { TrackedAlertEntity } from './interfaces/alert-entities/tracked-alert-entity';
import { Utilities } from './utilities';

// Shared by the client and admin alerts pages.
// Labels are looked up on every read (enums.<enumName>.<value>) so they follow the active language;
// the English text is the fallback until translations are loaded.

function localizedOption(enumName: string, value: string, english: string): SelectOption {
  return {
    value,
    get label(): string { return Utilities.enumLabel ? Utilities.enumLabel(enumName, value, english) : english; }
  } as SelectOption;
}

function localizedLabels<T extends string>(enumName: string, english: Record<T, string>): Record<T, string> {
  const labels = {} as Record<T, string>;
  (Object.keys(english) as T[]).forEach(value => Object.defineProperty(labels, value, {
    enumerable: true,
    get: () => Utilities.enumLabel ? Utilities.enumLabel(enumName, value, english[value]) : english[value]
  }));
  return labels;
}

export const ALERT_TYPE_GROUP_LABELS: Record<eAlertTypeGroup, string> = localizedLabels('alertTypeGroup', {
  [eAlertTypeGroup.SerialNumber]: 'Serial Number',
  [eAlertTypeGroup.Individual]: 'Individual',
  [eAlertTypeGroup.Business]: 'Business'
});

export const ALERT_SEVERITY_OPTIONS: SelectOption[] = [
  localizedOption('alertSeverity', eAlertSeverity.Low, 'Low'),
  localizedOption('alertSeverity', eAlertSeverity.Medium, 'Medium'),
  localizedOption('alertSeverity', eAlertSeverity.High, 'High'),
  localizedOption('alertSeverity', eAlertSeverity.Severe, 'Severe')
];

export const ALERT_STATUS_OPTIONS: SelectOption[] = [
  localizedOption('alertStatus', eAlertStatus.New, 'New'),
  localizedOption('alertStatus', eAlertStatus.Assigned, 'Assigned'),
  localizedOption('alertStatus', eAlertStatus.Resolved, 'Resolved')
];

// "Tracked Item" filter; each type shows its own fields in the alerts filter
export const TRACKED_ITEM_OPTIONS: SelectOption[] = [
  localizedOption('trackedAlertTypeEntity', eTrackedAlertTypeEntity.SerialNumber, 'Serial Number'),
  localizedOption('trackedAlertTypeEntity', eTrackedAlertTypeEntity.Individual, 'Individual'),
  localizedOption('trackedAlertTypeEntity', eTrackedAlertTypeEntity.Business, 'Business')
];

// CSS modifier for the severity/status pills defined in common.css
export function severityClass(severity: eAlertSeverity): string {
  return `alert-pill-${severity.toLowerCase()}`;
}

export function statusClass(status: eAlertStatus): string {
  return `alert-pill-status-${status.toLowerCase()}`;
}

// Human-readable name for whatever the alert is tracking (serial number, person or business)
export function trackedEntityDisplayName(entity?: TrackedAlertEntity | null): string {
  if (!entity) {
    return '';
  }

  switch (entity.trackedAlertEntityTypeID) {
    case eTrackedAlertTypeEntity.SerialNumber:
      return entity.serialNumber ?? '';
    case eTrackedAlertTypeEntity.Business:
      return entity.businessName ?? entity.businessNumber ?? '';
    case eTrackedAlertTypeEntity.Individual:
      return [entity.firstName, entity.middleName, entity.lastName].filter(n => !!n).join(' ');
    default:
      return '';
  }
}

// Whether new alerts are raised about a tracked item. Older APIs don't send isTracked, so fall back to
// the end date, where none means ongoing.
export function isTrackingActive(entity: TrackedAlertEntity): boolean {
  if (entity.isTracked !== undefined) {
    return entity.isTracked;
  }

  return entity.trackingEndDate === undefined || entity.trackingEndDate === null || new Date(entity.trackingEndDate) > new Date();
}
